/**
 * @author       yontaek@zumper.com (Yon Taek Chung)
 * @copyright    Copyright (c) 2022, Zumper
 * @description  currentSearch selectors
 */

import type { RentalDataPoint } from '@zcode/bizos'
import type { PriceChartData, State } from '@zcode/state-type'
import { compose } from 'redux'

export const selectCurrentSearch = (state: State) => state.currentSearch

export const selectLocation = compose((state) => state.location, selectCurrentSearch)

export const selectListables = compose((state) => state.listables, selectCurrentSearch)

export const selectAllNearbyHoods = compose(
  (currentSearch) => currentSearch.allNearbyHoods,
  selectCurrentSearch
)

export const selectCity = compose((currentSearch) => currentSearch.city, selectCurrentSearch)

export const selectListablesMatching = compose((state) => state.matching, selectListables)

export const selectIsFromPaidMarketing = compose(
  (currentSearch) => currentSearch.isFromPaidMarketing,
  selectCurrentSearch
)

export const selectUrl = compose((state) => state.url, selectLocation)

export const selectBox = compose((state) => state.box, selectLocation)

export const selectCityName = compose((state) => state?.cityName, selectLocation)

export const selectHoodName = compose((state) => state?.hoodName, selectLocation)

export const selectNearbyHoods = compose(
  (allNearbyHoods) => allNearbyHoods?.nearbyHoods,
  selectAllNearbyHoods
)

export const selectListablesCountsByPrice = compose(
  (currentSearch) => currentSearch.countsBy.minPrice,
  selectCurrentSearch
)

export const selectRentResearch = compose(
  (state: State['currentSearch']) => state.rentalData,
  selectCurrentSearch
)

export const selectRentResearchData = compose((state: State['currentSearch']['rentalData']) => {
  return state.data
}, selectRentResearch)

export const selectAverageRentData = compose(
  (state: State['currentSearch']['rentalData']['data']) => {
    if (!state) {
      return []
    }
    const mapBedAverageRent = (
      key: 'oneBeds' | 'twoBeds' | 'threeBeds' | 'fourBeds' | 'studio'
    ) => {
      if (!(state.bed && state.bed[key])) {
        return {
          series: [],
          labels: [],
          uncertain: [],
        }
      }
      const series: readonly RentalDataPoint[] = state.bed[key].monthlyMedianRent ?? []
      const labels = series.map((_series) => _series.x)
      const uncertain = series.map((_) => false)
      return {
        series,
        labels,
        uncertain,
      }
    }
    return [
      mapBedAverageRent('studio'),
      mapBedAverageRent('oneBeds'),
      mapBedAverageRent('twoBeds'),
      mapBedAverageRent('threeBeds'),
      mapBedAverageRent('fourBeds'),
    ]
  },
  selectRentResearchData
)
